//#region src/M2Error.ts
/**
* Custom error class for m2c2kit errors.
*
* @remarks This is the same class as in the m2c2kit core package. This simple
* code is copied from that package to avoid taking a dependency on it.
*/
var M2Error = class extends Error {
	/**
	* @param message - The error message.
	* @param options - Optional object containing an error 'cause' for error chaining.
	*/
	constructor(message, options) {
		super(message);
		if (options && "cause" in options) this.cause = options.cause;
		this.name = "M2Error";
		if (new.target) Object.setPrototypeOf(this, new.target.prototype);
		if ("captureStackTrace" in Error && typeof Error.captureStackTrace === "function") Error.captureStackTrace(this, new.target);
		else if (!this.stack) this.stack = new Error(message).stack;
	}
	/**
	* Structured JSON representation for logging, telemetry, and transport.
	*/
	toJSON() {
		const json = {
			name: this.name,
			message: this.message,
			stack: this.stack
		};
		if (this.cause !== void 0) if (this.cause instanceof Error) {
			const causeWithToJSON = this.cause;
			if (typeof causeWithToJSON.toJSON === "function") json.cause = causeWithToJSON.toJSON();
			else {
				const serialized = {
					name: this.cause.name,
					message: this.cause.message,
					stack: this.cause.stack
				};
				const causeRecord = this.cause;
				for (const key of Object.keys(causeRecord)) if (!(key in serialized)) serialized[key] = causeRecord[key];
				json.cause = serialized;
			}
		} else json.cause = this.cause;
		const thisRecord = this;
		for (const key of Object.keys(thisRecord)) if (!(key in json)) json[key] = thisRecord[key];
		return json;
	}
};
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/objectSpread2.js
function ownKeys(e, r) {
	var t = Object.keys(e);
	if (Object.getOwnPropertySymbols) {
		var o = Object.getOwnPropertySymbols(e);
		r && (o = o.filter(function(r) {
			return Object.getOwnPropertyDescriptor(e, r).enumerable;
		})), t.push.apply(t, o);
	}
	return t;
}
function _objectSpread2(e) {
	for (var r = 1; r < arguments.length; r++) {
		var t = null != arguments[r] ? arguments[r] : {};
		r % 2 ? ownKeys(Object(t), !0).forEach(function(r) {
			_defineProperty(e, r, t[r]);
		}) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function(r) {
			Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
		});
	}
	return e;
}
//#endregion
//#region src/ChainBuilder.ts
const CHAIN_REGISTRY = /* @__PURE__ */ new Map();
const MAX_CHAIN_REGISTRY_SIZE = 1e4;
let CHAIN_COUNTER = 0;
function getChainOps(id) {
	return CHAIN_REGISTRY.get(id);
}
function clearChainOps(id) {
	CHAIN_REGISTRY.delete(id);
}
function makeChainFn() {
	const fn = ((dataCalc) => {
		if (!dataCalc) return void 0;
		let current = dataCalc;
		for (const op of fn.ops) {
			const method = current[op.name];
			if (typeof method !== "function") throw new M2Error(`chain: method ${op.name} does not exist on DataCalc`);
			const res = method.apply(current, op.args);
			if (res instanceof DataCalc) {
				current = res;
				continue;
			}
			return res;
		}
	});
	fn.ops = [];
	const record = (name, args) => {
		fn.ops.push({
			name,
			args
		});
		return fn;
	};
	fn.arrange = (...variables) => record("arrange", variables);
	fn.slice = (start, end) => record("slice", [start, end]);
	fn.pull = (variable) => record("pull", [variable]);
	fn.filter = (predicate) => record("filter", [predicate]);
	fn.mutate = (mutations) => record("mutate", [mutations]);
	fn.select = (...variables) => record("select", variables);
	fn.groupBy = (...groups) => record("groupBy", groups);
	fn.ungroup = () => record("ungroup", []);
	fn.rename = (renames) => record("rename", [renames]);
	fn.distinct = () => record("distinct", []);
	Object.defineProperty(fn, "length", {
		configurable: true,
		get: function() {
			try {
				const id = `c${++CHAIN_COUNTER}`;
				CHAIN_REGISTRY.set(id, (fn.ops || []).map((o) => _objectSpread2({}, o)));
				if (CHAIN_REGISTRY.size > MAX_CHAIN_REGISTRY_SIZE) {
					const firstKey = CHAIN_REGISTRY.keys().next().value;
					if (firstKey) CHAIN_REGISTRY.delete(firstKey);
				}
				return `__CHAIN_EXPR__[${id}]`;
			} catch (err) {
				return;
			}
		}
	});
	return fn;
}
/**
* Adds an `arrange` operation to the chain for use in `summarize()`.
*
* Sorts the observations based on one or more variables. Variables may be
* prefixed with `-` for descending order.
*
* Mirrors the instance method {@link DataCalc.arrange()}
*
* @param variables Names of variables to sort by, prefixed with '-' for descending order
* @returns A chain function with the arrange operation added
* @example
* arrange('a', '-b')
*/
function arrange(...variables) {
	return makeChainFn().arrange(...variables);
}
/**
* Adds a `slice` operation to the chain for use in `summarize()`.
*
* Subsets observations by index range.
* The `start` index is inclusive and `end` is exclusive when provided.
*
* Mirrors the instance method {@link DataCalc.slice()}
*
* @param start Inclusive start index
* @param end Optional exclusive end index
* @returns A chain function with the slice operation added
* @example
* slice(0, 10)
*/
function slice(start, end) {
	return makeChainFn().slice(start, end);
}
/**
* Adds a `pull(variable)` operation to the chain for use in `summarize()`.
*
* Extracts a single variable from the data. If the variable length is 1 a
* scalar is returned, otherwise an array of values is returned.
*
* Mirrors the instance method {@link DataCalc.pull()}
*
* @param variable Name of the variable to pull
* @returns A chain function with the pull operation added
* @example
* pull('response_time')
*/
function pull(variable) {
	return makeChainFn().pull(variable);
}
/**
* Adds a `filter(predicate)` operation to the chain for use in `summarize()`.
*
* Filters observations based on a predicate function.
*
* Mirrors the instance method {@link DataCalc.filter()}
*
* @param predicate A function that returns true for observations to keep
* @returns A chain function with the filter operation added
* @example
* filter(obs => obs.correct)
*/
function filter(predicate) {
	return makeChainFn().filter(predicate);
}
/**
* Adds a `mutate(mutations)` operation to the chain for use in `summarize()`.
*
* Adds new variables to observations based on the provided mutation options.
* `mutations` is an object where keys are the names of the new variables and
* values are functions that take an observation and return the value for the
* new variable.
*
* Mirrors the instance method {@link DataCalc.mutate()}
*
* @param mutations Object mapping new variable names to transform functions
* @returns A chain function with the mutate operation added
* @example
* mutate({ doubledA: obs => obs.a * 2 })
*/
function mutate(mutations) {
	return makeChainFn().mutate(mutations);
}
/**
* Adds a `select(...variables)` operation to the chain for use in `summarize()`.
*
* Selects specific variables to keep in the dataset. Variables prefixed with
* `-` will be excluded from the result.
*
* Mirrors the instance method {@link DataCalc.select()}
*
* @param variables Names of variables to select; prefix with '-' to exclude
* @returns A chain function with the select operation added
* @example
* select('a', 'c')
*/
function select(...variables) {
	return makeChainFn().select(...variables);
}
/**
* Adds a `groupBy(...groups)` operation to the chain for use in `summarize()`.
*
* Groups observations by one or more variables. This is used with
* `summarize()` to calculate summaries by group. Grouping variables must be
* primitive values (string, number, boolean).
*
* Mirrors the instance method {@link DataCalc.groupBy()}
*
* @param groups Variable names to group by
* @returns A chain function with the groupBy operation added
* @example
* groupBy('condition')
*/
function groupBy(...groups) {
	return makeChainFn().groupBy(...groups);
}
/**
* Adds an `ungroup()` operation to the chain for use in `summarize()`.
*
* Ungroups observations so subsequent operations are applied globally.
*
* Mirrors the instance method {@link DataCalc.ungroup()}
*
* @returns A chain function with the ungroup operation added
*/
function ungroup() {
	return makeChainFn().ungroup();
}
/**
* Adds a `rename(renames)` operation to the chain for use in `summarize()`.
*
* Renames variables in the observations. `renames` should be an object
* mapping new variable names to old variable names.
*
* Mirrors the instance method {@link DataCalc.rename()}
*
* @param renames Object mapping new variable names to old variable names
* @returns A chain function with the rename operation added
* @example
* rename({ x: 'a', z: 'c' })
*/
function rename(renames) {
	return makeChainFn().rename(renames);
}
/**
* Adds a `distinct()` operation to the chain for use in `summarize()`.
*
* Keeps only unique observations.
*
* Mirrors the instance method {@link DataCalc.distinct()}
*
* @returns A chain function with the distinct operation added
*/
function distinct() {
	return makeChainFn().distinct();
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/objectWithoutPropertiesLoose.js
function _objectWithoutPropertiesLoose(r, e) {
	if (null == r) return {};
	var t = {};
	for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
		if (e.includes(n)) continue;
		t[n] = r[n];
	}
	return t;
}
//#endregion
//#region \0@oxc-project+runtime@0.134.0/helpers/esm/objectWithoutProperties.js
function _objectWithoutProperties(e, t) {
	if (null == e) return {};
	var o, r, i = _objectWithoutPropertiesLoose(e, t);
	if (Object.getOwnPropertySymbols) {
		var s = Object.getOwnPropertySymbols(e);
		for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
	}
	return i;
}
//#endregion
//#region src/DataCalc.ts
const _excluded = ["get", "set"];
var DataCalc = class DataCalc {
	/**
	* A class for transformation and calculation of m2c2kit data.
	*
	* @remarks The purpose is to provide a simple and intuitive interface for
	* assessments to score and summarize their own data. It is not meant for
	* data analysis or statistical modeling. The idiomatic approach is based on the
	* dplyr R package.
	*
	* @param data - An array of observations, where each observation is a set of
	* key-value pairs of variable names and values.
	* @param options - Options, such as groups to group the data by
	* @example
	* ```js
	* const dc = new DataCalc(gameData.trials);
	* const mean_response_time_correct_trials = dc
	*  .filter((obs) => obs.correct_response_index === obs.user_response_index)
	*  .summarize({ mean_rt: mean("response_time_duration_ms") })
	*  .pull("mean_rt");
	* ```
	*/
	constructor(data, options) {
		this._groups = new Array();
		this._warnings = false;
		if (!Array.isArray(data)) throw new M2Error("DataCalc constructor expects an array of observations as first argument");
		for (let i = 0; i < data.length; i++) if (data[i] === null || typeof data[i] !== "object" || Array.isArray(data[i])) throw new M2Error(`DataCalc constructor expects all elements to be objects (observations). Element at index ${i} is ${typeof data[i]}. Element: ${JSON.stringify(data[i])}`);
		this._observations = this.deepCopy(data);
		if (!(options === null || options === void 0 ? void 0 : options.skipNormalization)) {
			const allVariables = /* @__PURE__ */ new Set();
			for (const observation of data) for (const key of Object.keys(observation)) allVariables.add(key);
			for (const observation of this._observations) for (const variable of allVariables) if (!(variable in observation)) observation[variable] = null;
		}
		if (options === null || options === void 0 ? void 0 : options.groups) this._groups = Array.from(options.groups);
		if (options === null || options === void 0 ? void 0 : options.warnings) this._warnings = true;
	}
	/**
	* Returns the groups in the data.
	*/
	get groups() {
		return this._groups;
	}
	/**
	* Returns the observations in the data.
	*
	* @remarks An observation is conceptually similar to a row in a dataset,
	* where the keys are the variable names and the values are the variable values.
	*/
	get observations() {
		return this._observations;
	}
	/**
	* Alias for the observations property.
	*/
	get rows() {
		return this._observations;
	}
	/**
	* Returns a single variable from the data.
	*
	* @remarks If the variable length is 1, the value is returned. If the
	* variable has length > 1, an array of values is returned. If an empty
	* dataset is provided, `null` is returned and a warning is logged.
	*
	* @param variable - Name of variable to pull from the data
	* @returns the value of the variable
	*
	* @example
	* ```js
	* const d = [{ a: 1, b: 2, c: 3 }];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.pull("c")
	* ); // 3
	* ```
	*/
	pull(variable) {
		if (this._observations.length === 0) {
			if (this._warnings) console.warn(`DataCalc.pull(): No observations available to pull variable "${variable}" from. Returning null.`);
			return null;
		}
		this.verifyObservationsContainVariable(variable);
		const values = this._observations.map((o) => o[variable]);
		if (values.length === 1) return values[0];
		return values;
	}
	/**
	* Returns a single variable value from the data, throwing an error if more than one row exists.
	*
	* @remarks If the variable length is 1, the value is returned. If the
	* variable has length > 1, an error is thrown. If an empty dataset is
	* provided, `null` is returned and a warning is logged.
	*
	* @param variable - Name of variable to pull from the data
	* @returns the value of the variable
	*
	* @example
	* ```js
	* const d = [{ a: 1, b: 2, c: 3 }];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.pullScalar("c")
	* ); // 3
	* ```
	*/
	pullScalar(variable) {
		if (this._observations.length === 0) {
			if (this._warnings) console.warn(`DataCalc.pullScalar(): No observations available to pull variable "${variable}" from. Returning null.`);
			return null;
		}
		if (this._observations.length > 1) throw new M2Error(`DataCalc.pullScalar(): Expected 1 observation, but found ${this._observations.length}. Use pull() or pullArray() instead.`);
		this.verifyObservationsContainVariable(variable);
		return this._observations[0][variable];
	}
	/**
	* Returns a variable from the data as an array, even if there is only one row.
	*
	* @remarks If an empty dataset is provided, `null` is returned and a warning is logged.
	*
	* @param variable - Name of variable to pull from the data
	* @returns an array of values for the variable
	*
	* @example
	* ```js
	* const d = [{ a: 1, b: 2, c: 3 }];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.pullArray("c")
	* ); // [3]
	* ```
	*/
	pullArray(variable) {
		if (this._observations.length === 0) {
			if (this._warnings) console.warn(`DataCalc.pullArray(): No observations available to pull variable "${variable}" from. Returning null.`);
			return null;
		}
		this.verifyObservationsContainVariable(variable);
		return this._observations.map((o) => o[variable]);
	}
	/**
	* Returns the number of observations in the data.
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 }
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.length
	* ); // 2
	* ```
	*/
	get length() {
		return this._observations.length;
	}
	/**
	* Filters observations based on a predicate function.
	*
	* @param predicate - A function that returns true for observations to keep and
	* false for observations to discard
	* @returns A new `DataCalc` object with only the observations that pass the
	* predicate function
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(dc.filter((obs) => obs.b >= 3).observations);
	* // [ { a: 0, b: 8, c: 3 }, { a: 9, b: 4, c: 7 } ]
	* ```
	*/
	filter(predicate) {
		if (this._groups.length > 0) throw new M2Error(`filter() cannot be used on grouped data. The data are currently grouped by ${this._groups.join(", ")}. Ungroup the data first using ungroup().`);
		return new DataCalc(this._observations.filter(predicate), {
			groups: this._groups,
			warnings: this._warnings,
			skipNormalization: true
		});
	}
	/**
	* Groups observations by one or more variables.
	*
	* @remarks This is used with the `summarize()` method to calculate summaries
	* by group.
	*
	* @param groups - variable names to group by. Grouping variables must be
	* primitive values (string, number, boolean).
	* @returns A new `DataCalc` object with the observations grouped by one or
	* more variables
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	*   { a: 5, b: 0, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* const grouped = dc.groupBy("c");
	* // subsequent summarize operations will be performed separately by
	* // each unique level of c, in this case, 3 and 7
	* ```
	*/
	groupBy(...groups) {
		groups.forEach((group) => {
			this.verifyObservationsContainVariable(group);
			for (let i = 0; i < this._observations.length; i++) {
				const val = this._observations[i][group];
				if (val === null) continue;
				const t = typeof val;
				if (t !== "number" && t !== "string" && t !== "boolean") throw new M2Error(`groupBy(): variable "${group}" contains non-primitive value at index ${i} (type=${t}). Only number, string, boolean, or null are allowed for grouping.`);
			}
		});
		return new DataCalc(this._observations, {
			groups,
			skipNormalization: true
		});
	}
	/**
	* Ungroups observations.
	*
	* @returns A new DataCalc object with the observations ungrouped
	*/
	ungroup() {
		return new DataCalc(this._observations, { skipNormalization: true });
	}
	/**
	* Adds new variables to the observations based on the provided mutation options.
	*
	* @param mutations - An object where the keys are the names of the new variables
	* and the values are functions that take an observation and return the value
	* for the new variable.
	* @returns A new DataCalc object with the new variables added to the observations.
	*
	* @example
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.mutate({ doubledA: (obs) => obs.a * 2 }).observations
	* );
	* // [ { a: 1, b: 2, c: 3, doubledA: 2 },
	* //   { a: 0, b: 8, c: 3, doubledA: 0 },
	* //   { a: 9, b: 4, c: 7, doubledA: 18 } ]
	*/
	mutate(mutations) {
		if (this._groups.length > 0) throw new M2Error(`mutate() cannot be used on grouped data. The data are currently grouped by ${this._groups.join(", ")}. Ungroup the data first using ungroup().`);
		return new DataCalc(this._observations.map((observation) => {
			let newObservation = _objectSpread2({}, observation);
			for (const [newVariable, transformFunction] of Object.entries(mutations)) newObservation = _objectSpread2(_objectSpread2({}, newObservation), {}, { [newVariable]: transformFunction(observation) });
			return newObservation;
		}), {
			groups: this._groups,
			warnings: this._warnings
		});
	}
	/**
	* Calculates summaries of the data.
	*
	* @param summarizations - An object where the keys are the names of the new
	* variables and the values are `DataCalc` summary functions: `sum()`,
	* `mean()`, `median()`, `variance()`, `sd()`, `min()`, `max()`, or `n()`.
	* The summary functions take a variable name as a string, or alternatively,
	* a value or array of values to summarize.
	* @returns A new `DataCalc` object with the new summary variables.
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	*   { a: 5, b: 0, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     meanA: mean("a"),
	*     varA: variance("a"),
	*     totalB: sum("b")
	*   }).observations
	* );
	* // [ { meanA: 3.75, varA: 16.916666666666668, totalB: 14 } ]
	*
	* console.log(
	*   dc.summarize({
	*    filteredTotalC: sum(dc.filter(obs => obs.b > 2).pull("c"))
	*  }).observations
	* );
	* // [ { filteredTotalC: 10 } ]
	* ```
	*
	* @remarks Within a `summarize()` call, regular arithmetic operations
	* (+, -, *, /, etc.) must not be used directly on `DataCalc` objects
	* or summary functions. This is not allowed:
	*
	* @example
	* ```js
	* dc.summarize({
	*   meanA: mean("a"),
	*   // The below will cause an error because it attempts to add a number
	*   // to a DataCalc object, which is not supported
	*   totalBPlus5: sum("b") + 5
	* })
	* ```
	*
	* Instead, use the `DataCalc` helper functions such as `add` to perform
	* calculations within summary results. For example, to add 5 to the sum
	* of "b", you can do:
	* @example
	* ```js
	* dc.summarize({
	*   meanA: mean("a"),
	*   totalBPlus5: sum("b").add(5)
	* })
	* ```
	*/
	summarize(summarizations) {
		if (this._groups.length === 0) {
			const obs = {};
			for (const [newVariable, value] of Object.entries(summarizations)) if (typeof value === "object" && value !== null && "summarizeFunction" in value) {
				const summarizeOperation = value;
				obs[newVariable] = summarizeOperation.summarizeFunction(this, summarizeOperation.parameters, summarizeOperation.options);
			} else if (typeof value === "function") try {
				const res = value(this);
				if (typeof res === "function") throw new M2Error(`summarize(): lazy callback for ${newVariable} returned a function; expected a value or array of values.`);
				if (res instanceof DataCalc) throw new M2Error(`summarize(): lazy callback for ${newVariable} returned a DataCalc; expected a value or array of values.`);
				obs[newVariable] = res;
			} catch (err) {
				throw new M2Error(`summarize(): lazy callback for ${newVariable} threw an error: ${err && err.message ? err.message : String(err)}`);
			}
			else {
				if (typeof value === "string") {
					const matches = Array.from(Helpers.matchAll(value, /__CHAIN_EXPR__\[(.*?)\]/g));
					if (matches.length > 0) {
						let sum = 0;
						for (const m of matches) {
							const payload = m[1];
							let ops = getChainOps(payload);
							if (!ops) try {
								ops = JSON.parse(decodeURIComponent(payload));
							} catch (err) {
								throw new M2Error(`summarize(): failed to parse chain payload for ${newVariable}`);
							}
							let current = this;
							let evaluated = void 0;
							if (!ops) throw new M2Error(`summarize(): empty chain ops for ${newVariable}`);
							for (const op of ops) {
								const method = current[op.name];
								if (typeof method !== "function") throw new M2Error(`summarize(): chain method ${op.name} does not exist on DataCalc`);
								const res = method.apply(current, op.args);
								if (res instanceof DataCalc) {
									current = res;
									continue;
								}
								if (Array.isArray(res)) {
									evaluated = res.length;
									break;
								}
								if (typeof res === "boolean") {
									evaluated = res ? 1 : 0;
									break;
								}
								evaluated = typeof res === "number" ? res : Number(res);
								break;
							}
							if (evaluated === void 0) evaluated = current instanceof DataCalc ? current.length : 0;
							sum += evaluated;
						}
						let leftover = value;
						for (const m of matches) leftover = leftover.replace(m[0], "");
						const leftoverNum = Number(leftover);
						if (!Number.isNaN(leftoverNum)) sum += leftoverNum;
						obs[newVariable] = sum;
						continue;
					}
				}
				obs[newVariable] = value;
			}
			return new DataCalc([obs], {
				groups: this._groups,
				warnings: this._warnings
			});
		}
		return this.summarizeByGroups(summarizations);
	}
	summarizeByGroups(summarizations) {
		const groupMap = /* @__PURE__ */ new Map();
		this._observations.forEach((obs) => {
			const groupKey = this._groups.map((g) => String(obs[g])).join("|");
			if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
			const groupArray = groupMap.get(groupKey);
			if (groupArray) groupArray.push(obs);
			else groupMap.set(groupKey, [obs]);
		});
		const summarizedObservations = [];
		groupMap.forEach((groupObs, groupKey) => {
			const groupValues = groupKey.split("|");
			const firstObs = groupObs[0];
			const summaryObj = {};
			this._groups.forEach((group, i) => {
				const valueStr = groupValues[i];
				if (firstObs[group] === null) summaryObj[group] = null;
				else {
					const originalType = typeof firstObs[group];
					if (originalType === "number") summaryObj[group] = Number(valueStr);
					else if (originalType === "boolean") summaryObj[group] = valueStr === "true";
					else summaryObj[group] = valueStr;
				}
			});
			const groupDataCalc = new DataCalc(groupObs);
			for (const [newVariable, value] of Object.entries(summarizations)) if (typeof value === "object" && value !== null && "summarizeFunction" in value) {
				const summarizeOperation = value;
				summaryObj[newVariable] = summarizeOperation.summarizeFunction(groupDataCalc, summarizeOperation.parameters, summarizeOperation.options);
			} else if (typeof value === "function") try {
				const res = value(groupDataCalc);
				if (typeof res === "function") throw new M2Error(`summarize(): lazy callback for ${newVariable} returned a function; expected a value or array of values.`);
				if (res instanceof DataCalc) throw new M2Error(`summarize(): lazy callback for ${newVariable} returned a DataCalc; expected a value or array of values.`);
				summaryObj[newVariable] = res;
			} catch (err) {
				throw new M2Error(`summarize(): lazy callback for ${newVariable} threw an error: ${err && err.message ? err.message : String(err)}`);
			}
			else {
				if (typeof value === "string") {
					const matches = Array.from(Helpers.matchAll(value, /__CHAIN_EXPR__\[(.*?)\]/g));
					if (matches.length > 0) {
						let sum = 0;
						for (const m of matches) {
							const payload = m[1];
							let ops = getChainOps(payload);
							if (!ops) try {
								ops = JSON.parse(decodeURIComponent(payload));
							} catch (err) {
								throw new M2Error(`summarize(): failed to parse chain payload for ${newVariable}`);
							}
							let current = groupDataCalc;
							let evaluated = void 0;
							if (!ops) throw new M2Error(`summarize(): empty chain ops for ${newVariable}`);
							for (const op of ops) {
								const method = current[op.name];
								if (typeof method !== "function") throw new M2Error(`summarize(): chain method ${op.name} does not exist on DataCalc`);
								const res = method.apply(current, op.args);
								if (res instanceof DataCalc) {
									current = res;
									continue;
								}
								if (Array.isArray(res)) {
									evaluated = res.length;
									break;
								}
								if (typeof res === "boolean") {
									evaluated = res ? 1 : 0;
									break;
								}
								evaluated = typeof res === "number" ? res : Number(res);
								break;
							}
							if (evaluated === void 0) evaluated = current instanceof DataCalc ? current.length : 0;
							sum += evaluated;
						}
						let leftover = value;
						for (const m of matches) leftover = leftover.replace(m[0], "");
						const leftoverNum = Number(leftover);
						if (!Number.isNaN(leftoverNum)) sum += leftoverNum;
						summaryObj[newVariable] = sum;
						continue;
					}
				}
				summaryObj[newVariable] = value;
			}
			summarizedObservations.push(summaryObj);
		});
		return new DataCalc(summarizedObservations, {
			groups: this._groups,
			warnings: this._warnings
		});
	}
	/**
	* Selects specific variables to keep in the dataset.
	* Variables prefixed with "-" will be excluded from the result.
	*
	* @param variables - Names of variables to select; prefix with '-' to exclude instead
	* @returns A new DataCalc object with only the selected variables (minus excluded ones)
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3, d: 4 },
	*   { a: 5, b: 6, c: 7, d: 8 }
	* ];
	* const dc = new DataCalc(d);
	* // Keep a and c
	* console.log(dc.select("a", "c").observations);
	* // [ { a: 1, c: 3 }, { a: 5, c: 7 } ]
	* ```
	*/
	select(...variables) {
		const includeVars = [];
		const excludeVars = [];
		variables.forEach((variable) => {
			if (variable.startsWith("-")) excludeVars.push(variable.substring(1));
			else includeVars.push(variable);
		});
		[...includeVars.length > 0 ? includeVars : Object.keys(this._observations[0] || {}), ...excludeVars].forEach((variable) => {
			this.verifyObservationsContainVariable(variable);
		});
		const excludeSet = new Set(excludeVars);
		return new DataCalc(this._observations.map((observation) => {
			const newObservation = {};
			if (includeVars.length > 0) includeVars.forEach((variable) => {
				if (!excludeSet.has(variable)) newObservation[variable] = observation[variable];
			});
			else Object.keys(observation).forEach((key) => {
				if (!excludeSet.has(key)) newObservation[key] = observation[key];
			});
			return newObservation;
		}), {
			groups: this._groups,
			warnings: this._warnings
		});
	}
	/**
	* Arranges (sorts) the observations based on one or more variables.
	*
	* @param variables - Names of variables to sort by, prefixed with '-' for descending order
	* @returns A new DataCalc object with the observations sorted
	*
	* @example
	* ```js
	* const d = [
	*   { a: 5, b: 2 },
	*   { a: 3, b: 7 },
	*   { a: 5, b: 1 }
	* ];
	* const dc = new DataCalc(d);
	* // Sort by a (ascending), then by b (descending)
	* console.log(dc.arrange("a", "-b").observations);
	* // [ { a: 3, b: 7 }, { a: 5, b: 2 }, { a: 5, b: 1 } ]
	* ```
	*/
	arrange(...variables) {
		if (this._groups.length > 0) throw new M2Error(`arrange() cannot be used on grouped data. The data are currently grouped by ${this._groups.join(", ")}. Ungroup the data first using ungroup().`);
		return new DataCalc([...this._observations].sort((a, b) => {
			for (const variable of variables) {
				let varName = variable;
				let direction = 1;
				if (variable.startsWith("-")) {
					varName = variable.substring(1);
					direction = -1;
				}
				if (!(varName in a) || !(varName in b)) throw new M2Error(`arrange(): variable ${varName} does not exist in all observations`);
				const aVal = a[varName];
				const bVal = b[varName];
				if (typeof aVal !== typeof bVal) return direction * (String(aVal) < String(bVal) ? -1 : 1);
				if (aVal < bVal) return -1 * direction;
				if (aVal > bVal) return 1 * direction;
			}
			return 0;
		}), {
			groups: this._groups,
			warnings: this._warnings,
			skipNormalization: true
		});
	}
	/**
	* Keeps only unique/distinct observations.
	*
	* @returns A new `DataCalc` object with only unique observations
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 1, b: 2, c: 3 }, // Duplicate
	*   { a: 2, b: 3, c: 5 },
	*   { a: 1, b: 2, c: { name: "dog" } },
	*   { a: 1, b: 2, c: { name: "dog" } } // Duplicate with nested object
	* ];
	* const dc = new DataCalc(d);
	* console.log(dc.distinct().observations);
	* // [ { a: 1, b: 2, c: 3 }, { a: 2, b: 3, c: 5 }, { a: 1, b: 2, c: { name: "dog" } } ]
	* ```
	*/
	distinct() {
		const seen = /* @__PURE__ */ new Set();
		return new DataCalc(this._observations.filter((obs) => {
			const key = JSON.stringify(this.normalizeForComparison(obs));
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		}), {
			groups: this._groups,
			warnings: this._warnings,
			skipNormalization: true
		});
	}
	/**
	* Renames variables in the observations.
	*
	* @remarks If a target name in `renames` already exists in the dataset (and is not
	* simply the source name being renamed), the rename will overwrite the existing
	* variable. When warnings are enabled, a warning will be logged to make users aware
	* of the potential overwrite.
	*
	* @param renames - Object mapping new variable names to old variable names
	* @returns A new DataCalc object with renamed variables
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 4, b: 5, c: 6 }
	* ];
	* const dc = new DataCalc(d);
	* console.log(dc.rename({ x: 'a', z: 'c' }).observations);
	* // [ { x: 1, b: 2, z: 3 }, { x: 4, b: 5, z: 6 } ]
	* ```
	*/
	rename(renames) {
		if (this._observations.length === 0) throw new M2Error("Cannot rename variables on an empty dataset");
		Object.values(renames).forEach((oldName) => {
			this.verifyObservationsContainVariable(oldName);
		});
		if (this._observations.length > 0) {
			const existingKeys = new Set(Object.keys(this._observations[0]));
			const oldNames = new Set(Object.values(renames));
			const collisions = Object.keys(renames).filter((n) => existingKeys.has(n) && !oldNames.has(n));
			if (collisions.length > 0 && this._warnings) console.warn(`DataCalc.rename(): renaming will overwrite existing variables: ${collisions.join(", ")}`);
		}
		return new DataCalc(this._observations.map((observation) => {
			const newObservation = {};
			const newNames = new Set(Object.keys(renames));
			for (const [key, value] of Object.entries(observation)) {
				var _Object$entries$find;
				const newKey = (_Object$entries$find = Object.entries(renames).find(([, old]) => old === key)) === null || _Object$entries$find === void 0 ? void 0 : _Object$entries$find[0];
				if (newKey) newObservation[newKey] = value;
				else if (!newNames.has(key)) newObservation[key] = value;
			}
			return newObservation;
		}), {
			groups: this._groups,
			warnings: this._warnings
		});
	}
	/**
	* Performs an inner join with another DataCalc object.
	* Only rows with matching keys in both datasets are included.
	*
	* @param other - The other DataCalc object to join with
	* @param by - The variables to join on
	* @returns A new DataCalc object with joined observations
	*
	* @example
	* ```js
	* const d1 = [
	*   { id: 1, x: 'a' },
	*   { id: 2, x: 'b' },
	*   { id: 3, x: 'c' }
	* ];
	* const d2 = [
	*   { id: 1, y: 100 },
	*   { id: 2, y: 200 },
	*   { id: 4, y: 400 }
	* ];
	* const dc1 = new DataCalc(d1);
	* const dc2 = new DataCalc(d2);
	* console.log(dc1.innerJoin(dc2, ["id"]).observations);
	* // [ { id: 1, x: 'a', y: 100 }, { id: 2, x: 'b', y: 200 } ]
	* ```
	*/
	innerJoin(other, by) {
		if (this._groups.length > 0 || other._groups.length > 0) throw new M2Error(`innerJoin() cannot be used on grouped data. Ungroup the data first using ungroup().`);
		by.forEach((key) => {
			this.verifyObservationsContainVariable(key);
			other.verifyObservationsContainVariable(key);
		});
		const rightMap = /* @__PURE__ */ new Map();
		other.observations.forEach((obs) => {
			if (this.hasNullJoinKeys(obs, by)) return;
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(obs[k]))).join("|");
			const matches = rightMap.get(key) || [];
			matches.push(obs);
			rightMap.set(key, matches);
		});
		const result = [];
		this._observations.forEach((leftObs) => {
			if (this.hasNullJoinKeys(leftObs, by)) return;
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(leftObs[k]))).join("|");
			const rightMatches = rightMap.get(key) || [];
			if (rightMatches.length > 0) rightMatches.forEach((rightObs) => {
				const joinedObs = _objectSpread2({}, leftObs);
				Object.entries(rightObs).forEach(([k, v]) => {
					if (!by.includes(k)) joinedObs[k] = v;
				});
				result.push(joinedObs);
			});
		});
		return new DataCalc(result);
	}
	/**
	* Performs a left join with another DataCalc object.
	* All rows from the left dataset are included, along with matching rows from the right.
	*
	* @param other - The other DataCalc object to join with
	* @param by - The variables to join on
	* @returns A new DataCalc object with joined observations
	*
	* @example
	* ```js
	* const d1 = [
	*   { id: 1, x: 'a' },
	*   { id: 2, x: 'b' },
	*   { id: 3, x: 'c' }
	* ];
	* const d2 = [
	*   { id: 1, y: 100 },
	*   { id: 2, y: 200 }
	* ];
	* const dc1 = new DataCalc(d1);
	* const dc2 = new DataCalc(d2);
	* console.log(dc1.leftJoin(dc2, ["id"]).observations);
	* // [ { id: 1, x: 'a', y: 100 }, { id: 2, x: 'b', y: 200 }, { id: 3, x: 'c' } ]
	* ```
	*/
	leftJoin(other, by) {
		if (this._groups.length > 0 || other._groups.length > 0) throw new M2Error(`leftJoin() cannot be used on grouped data. Ungroup the data first using ungroup().`);
		by.forEach((key) => {
			this.verifyObservationsContainVariable(key);
			other.verifyObservationsContainVariable(key);
		});
		const rightMap = /* @__PURE__ */ new Map();
		other.observations.forEach((obs) => {
			if (this.hasNullJoinKeys(obs, by)) return;
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(obs[k]))).join("|");
			const matches = rightMap.get(key) || [];
			matches.push(obs);
			rightMap.set(key, matches);
		});
		const result = [];
		this._observations.forEach((leftObs) => {
			if (this.hasNullJoinKeys(leftObs, by)) {
				result.push(_objectSpread2({}, leftObs));
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(leftObs[k]))).join("|");
			const rightMatches = rightMap.get(key) || [];
			if (rightMatches.length > 0) rightMatches.forEach((rightObs) => {
				const joinedObs = _objectSpread2({}, leftObs);
				Object.entries(rightObs).forEach(([k, v]) => {
					if (!by.includes(k)) joinedObs[k] = v;
				});
				result.push(joinedObs);
			});
			else result.push(_objectSpread2({}, leftObs));
		});
		return new DataCalc(result);
	}
	/**
	* Performs a right join with another DataCalc object.
	* All rows from the right dataset are included, along with matching rows from the left.
	*
	* @param other - The other DataCalc object to join with
	* @param by - The variables to join on
	* @returns A new DataCalc object with joined observations
	*
	* @example
	* ```js
	* const d1 = [
	*   { id: 1, x: 'a' },
	*   { id: 2, x: 'b' }
	* ];
	* const d2 = [
	*   { id: 1, y: 100 },
	*   { id: 2, y: 200 },
	*   { id: 4, y: 400 }
	* ];
	* const dc1 = new DataCalc(d1);
	* const dc2 = new DataCalc(d2);
	* console.log(dc1.rightJoin(dc2, ["id"]).observations);
	* // [ { id: 1, x: 'a', y: 100 }, { id: 2, x: 'b', y: 200 }, { id: 4, y: 400 } ]
	* ```
	*/
	rightJoin(other, by) {
		if (this._groups.length > 0 || other._groups.length > 0) throw new M2Error(`rightJoin() cannot be used on grouped data. Ungroup the data first using ungroup().`);
		by.forEach((key) => {
			this.verifyObservationsContainVariable(key);
			other.verifyObservationsContainVariable(key);
		});
		const rightMap = /* @__PURE__ */ new Map();
		const rightObsWithNullKeys = [];
		other.observations.forEach((obs) => {
			if (this.hasNullJoinKeys(obs, by)) {
				rightObsWithNullKeys.push(obs);
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(obs[k]))).join("|");
			const matches = rightMap.get(key) || [];
			matches.push(obs);
			rightMap.set(key, matches);
		});
		const result = [];
		const processedRightKeys = /* @__PURE__ */ new Set();
		this._observations.forEach((leftObs) => {
			if (this.hasNullJoinKeys(leftObs, by)) return;
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(leftObs[k]))).join("|");
			const rightMatches = rightMap.get(key) || [];
			if (rightMatches.length > 0) {
				rightMatches.forEach((rightObs) => {
					const joinedObs = _objectSpread2({}, leftObs);
					Object.entries(rightObs).forEach(([k, v]) => {
						if (!by.includes(k)) joinedObs[k] = v;
					});
					result.push(joinedObs);
				});
				processedRightKeys.add(key);
			}
		});
		other.observations.forEach((rightObs) => {
			if (this.hasNullJoinKeys(rightObs, by)) {
				result.push(_objectSpread2({}, rightObs));
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(rightObs[k]))).join("|");
			if (!processedRightKeys.has(key)) {
				result.push(_objectSpread2({}, rightObs));
				processedRightKeys.add(key);
			}
		});
		return new DataCalc(result);
	}
	/**
	* Performs a full join with another DataCalc object.
	* All rows from both datasets are included.
	*
	* @param other - The other DataCalc object to join with
	* @param by - The variables to join on
	* @returns A new DataCalc object with joined observations
	*
	* @example
	* ```js
	* const d1 = [
	*   { id: 1, x: 'a' },
	*   { id: 2, x: 'b' },
	*   { id: 3, x: 'c' }
	* ];
	* const d2 = [
	*   { id: 1, y: 100 },
	*   { id: 2, y: 200 },
	*   { id: 4, y: 400 }
	* ];
	* const dc1 = new DataCalc(d1);
	* const dc2 = new DataCalc(d2);
	* console.log(dc1.fullJoin(dc2, ["id"]).observations);
	* // [
	* //   { id: 1, x: 'a', y: 100 },
	* //   { id: 2, x: 'b', y: 200 },
	* //   { id: 3, x: 'c' },
	* //   { id: 4, y: 400 }
	* // ]
	* ```
	*/
	fullJoin(other, by) {
		if (this._groups.length > 0 || other._groups.length > 0) throw new M2Error(`fullJoin() cannot be used on grouped data. Ungroup the data first using ungroup().`);
		by.forEach((key) => {
			this.verifyObservationsContainVariable(key);
			other.verifyObservationsContainVariable(key);
		});
		const rightMap = /* @__PURE__ */ new Map();
		const rightObsWithNullKeys = [];
		other.observations.forEach((obs) => {
			if (this.hasNullJoinKeys(obs, by)) {
				rightObsWithNullKeys.push(obs);
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(obs[k]))).join("|");
			const matches = rightMap.get(key) || [];
			matches.push(obs);
			rightMap.set(key, matches);
		});
		const result = [];
		const processedRightKeys = /* @__PURE__ */ new Set();
		this._observations.forEach((leftObs) => {
			if (this.hasNullJoinKeys(leftObs, by)) {
				result.push(_objectSpread2({}, leftObs));
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(leftObs[k]))).join("|");
			const rightMatches = rightMap.get(key) || [];
			if (rightMatches.length > 0) {
				rightMatches.forEach((rightObs) => {
					const joinedObs = _objectSpread2({}, leftObs);
					Object.entries(rightObs).forEach(([k, v]) => {
						if (!by.includes(k)) joinedObs[k] = v;
					});
					result.push(joinedObs);
				});
				processedRightKeys.add(key);
			} else result.push(_objectSpread2({}, leftObs));
		});
		other.observations.forEach((rightObs) => {
			if (this.hasNullJoinKeys(rightObs, by)) {
				result.push(_objectSpread2({}, rightObs));
				return;
			}
			const key = by.map((k) => JSON.stringify(this.normalizeForComparison(rightObs[k]))).join("|");
			if (!processedRightKeys.has(key)) {
				result.push(_objectSpread2({}, rightObs));
				processedRightKeys.add(key);
			}
		});
		return new DataCalc(result);
	}
	/**
	* Slice observations by position.
	*
	* @param start - Starting position (0-based). Negative values count from
	* the end.
	* @param end - Ending position (exclusive)
	* @returns A new DataCalc object with sliced observations
	*
	* @remarks If `end` is not provided, it will return a single observation at
	* `start` position. If `start` is beyond the length of observations,
	* it will return an empty DataCalc.
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2 },
	*   { a: 3, b: 4 },
	*   { a: 5, b: 6 },
	*   { a: 7, b: 8 }
	* ];
	* const dc = new DataCalc(d);
	* console.log(dc.slice(1, 3).observations);
	* // [ { a: 3, b: 4 }, { a: 5, b: 6 } ]
	* console.log(dc.slice(0).observations);
	* // [ { a: 1, b: 2 } ]
	* ```
	*/
	slice(start, end) {
		if (this._groups.length > 0) throw new M2Error(`slice() cannot be used on grouped data. Ungroup the data first using ungroup().`);
		let sliced;
		if (start >= this._observations.length) return new DataCalc([], {
			groups: this._groups,
			warnings: this._warnings,
			skipNormalization: true
		});
		if (end === void 0) {
			const index = start < 0 ? this._observations.length + start : start;
			sliced = [this._observations[index]];
		} else sliced = this._observations.slice(start, end);
		return new DataCalc(sliced, {
			groups: this._groups,
			warnings: this._warnings,
			skipNormalization: true
		});
	}
	/**
	* Combines observations from two DataCalc objects by rows.
	*
	* @param other - The other DataCalc object to bind with
	* @returns A new DataCalc object with combined observations
	*
	* @example
	* ```js
	* const d1 = [
	*   { a: 1, b: 2 },
	*   { a: 3, b: 4 }
	* ];
	* const d2 = [
	*   { a: 5, b: 6 },
	*   { a: 7, b: 8 }
	* ];
	* const dc1 = new DataCalc(d1);
	* const dc2 = new DataCalc(d2);
	* console.log(dc1.bindRows(dc2).observations);
	* // [ { a: 1, b: 2 }, { a: 3, b: 4 }, { a: 5, b: 6 }, { a: 7, b: 8 } ]
	* ```
	*/
	bindRows(other) {
		if (this._observations.length > 0 && other.observations.length > 0) {
			const thisVariables = new Set(Object.keys(this._observations[0]));
			const otherVariables = new Set(Object.keys(other.observations[0]));
			[...thisVariables].filter((variable) => otherVariables.has(variable)).forEach((variable) => {
				const thisType = this.getVariableType(variable);
				const otherType = other.getVariableType(variable);
				if (thisType !== otherType) console.warn(`Warning: bindRows() is combining datasets with different data types for variable '${variable}'. Left dataset has type '${thisType}' and right dataset has type '${otherType}'.`);
			});
		}
		return new DataCalc([...this._observations, ...other.observations]);
	}
	/**
	* Helper method to determine the primary type of a variable across observations
	* @internal
	*
	* @param variable - The variable name to check
	* @returns The most common type for the variable or 'mixed' if no clear type exists
	*/
	getVariableType(variable) {
		if (this._observations.length === 0) return "unknown";
		const typeCounts = {};
		this._observations.forEach((obs) => {
			if (variable in obs) {
				const value = obs[variable];
				const type = value === null ? "null" : Array.isArray(value) ? "array" : typeof value;
				typeCounts[type] = (typeCounts[type] || 0) + 1;
			}
		});
		let maxCount = 0;
		let dominantType = "unknown";
		for (const [type, count] of Object.entries(typeCounts)) if (count > maxCount) {
			maxCount = count;
			dominantType = type;
		}
		return dominantType;
	}
	/**
	* Verifies that the variable exists in each observation in the data.
	*
	* @remarks Throws an error if the variable does not exist in each
	* observation. This is not meant to be called by users of the library, but
	* is used internally.
	* @internal
	*
	* @param variable - The variable to check for
	*/
	verifyObservationsContainVariable(variable) {
		if (!this._observations.every((observation) => variable in observation)) throw new M2Error(`Variable ${variable} does not exist for each item (row) in the data array.`);
	}
	/**
	* Checks if the variable exists for at least one observation in the data.
	*
	* @remarks This is not meant to be called by users of the library, but
	* is used internally.
	* @internal
	*
	* @param variable - The variable to check for
	* @returns true if the variable exists in at least one observation, false
	* otherwise
	*/
	variableExists(variable) {
		return this._observations.some((observation) => variable in observation);
	}
	/**
	* Checks if a value is a non-missing numeric value.
	*
	* @remarks A non-missing numeric value is a value that is a number and is
	* not NaN or infinite.
	*
	* @param value - The value to check
	* @returns true if the value is a non-missing numeric value, false otherwise
	*/
	isNonMissingNumeric(value) {
		return typeof value === "number" && !isNaN(value) && isFinite(value);
	}
	/**
	* Checks if a value is a missing numeric value.
	*
	* @remarks A missing numeric value is a number that is NaN or infinite, or any
	* value that is null or undefined. Thus, a null or undefined value is
	* considered to be a missing numeric value.
	*
	* @param value - The value to check
	* @returns true if the value is a missing numeric value, false otherwise
	*/
	isMissingNumeric(value) {
		return typeof value === "number" && (isNaN(value) || !isFinite(value)) || value === null || typeof value === "undefined";
	}
	/**
	* Normalizes an object for stable comparison by sorting keys
	* @internal
	*
	* @remarks Normalizing is needed to handle situations where objects have the
	* same properties but in different orders because we are using
	* JSON.stringify() for comparison.
	*/
	normalizeForComparison(obj) {
		if (obj === null || typeof obj !== "object") return obj;
		if (Array.isArray(obj)) return obj.map((item) => this.normalizeForComparison(item));
		return Object.keys(obj).sort().reduce((result, key) => {
			result[key] = this.normalizeForComparison(obj[key]);
			return result;
		}, {});
	}
	/**
	* Creates a deep copy of an object.
	* @internal
	*
	* @remarks We create a deep copy of the object, in our case an instance
	* of `DataCalc`, to ensure that we are working with a new object
	* without any references to the original object. This is important
	* to avoid unintended side effects when modifying an object.
	*
	* @param source - object to copy
	* @param map - map of objects that have already been copied
	* @returns a deep copy of the object
	*/
	deepCopy(source, map = /* @__PURE__ */ new WeakMap()) {
		const sClone = globalThis.structuredClone;
		if (typeof sClone === "function") try {
			const proto = source && typeof source === "object" ? Object.getPrototypeOf(source) : null;
			if (Array.isArray(source) || proto === Object.prototype || proto === null) {
				if (!Object.values(Object.getOwnPropertyDescriptors(source)).some((d) => typeof d.get === "function" || typeof d.set === "function")) return sClone(source);
			}
		} catch (_unused) {}
		if (source === null || typeof source !== "object") return source;
		if (map.has(source)) return map.get(source);
		const copy = Array.isArray(source) ? [] : Object.create(Object.getPrototypeOf(source));
		map.set(source, copy);
		const keys = [...Object.getOwnPropertyNames(source), ...Object.getOwnPropertySymbols(source)];
		for (const key of keys) {
			const descriptor = Object.getOwnPropertyDescriptor(source, key);
			if (descriptor) {
				const { get, set } = descriptor, descWithoutAccessors = _objectWithoutProperties(descriptor, _excluded);
				Object.defineProperty(copy, key, _objectSpread2(_objectSpread2({}, descWithoutAccessors), {}, { value: this.deepCopy(source[key], map) }));
			}
		}
		return copy;
	}
	/**
	* Checks if an observation has null or undefined values in any of the join columns.
	* @internal
	*
	* @param obs - The observation to check
	* @param keys - The join columns to check
	* @returns true if any join column has a null or undefined value
	*/
	hasNullJoinKeys(obs, keys) {
		return keys.some((key) => obs[key] === null || obs[key] === void 0);
	}
};
const Helpers = { 
/**
* Returns an iterator of all results matching a string against a regular expression.
* ES2020-compliant matchAll utility, Target: ES2017 minimum
*
* @param str - The string to search for matches.
* @param regexp - A regular expression object (must have the 'g' flag) or a string.
* @returns An iterator yielding RegExp match arrays.
*/
matchAll: function* (str, regexp) {
	if (str == null) throw new TypeError("String is null or not defined");
	const stringValue = String(str);
	let matcher;
	if (regexp instanceof RegExp) {
		if (!regexp.global) throw new TypeError("matchAll called with a non-global RegExp argument");
		matcher = new RegExp(regexp.source, regexp.flags);
	} else matcher = new RegExp(regexp, "g");
	let match;
	while ((match = matcher.exec(stringValue)) !== null) {
		yield match;
		if (match[0].length === 0) {
			if (matcher.unicode) {
				const charCode = stringValue.charCodeAt(matcher.lastIndex);
				if (charCode >= 55296 && charCode <= 56319) matcher.lastIndex++;
			}
			matcher.lastIndex++;
		}
	}
} };
//#endregion
//#region src/SummarizeOperation.ts
const PRECEDENCE = {
	"+": 1,
	"-": 1,
	"*": 2,
	"/": 2,
	"^": 3
};
/**
* `SummarizeOperation` is a builder for summary expressions.
*
* It stores a token stream of operands and operators. Operands can be:
* - a numeric literal
* - another `SummarizeOperation` (including leaf reducers like `mean()` or `sum()`)
*
* The instance exposes arithmetic methods (`add`, `sub`, `mul`, `div`, `pow`)
* which append operator + operand tokens and return a new `SummarizeOperation`.
*
* When `DataCalc.summarize()` calls the `.summarizeFunction(dc, params, opts)`,
* the token stream is evaluated with correct operator precedence.
*
* A leaf reducer should create a `SummarizeOperation` with a `leafFn`; the token
* stream for a leaf starts as a single operand referencing the leaf itself.
*/
var SummarizeOperation = class SummarizeOperation {
	constructor(leafFn, parameters, options, tokens) {
		this.leafFn = leafFn;
		this.parameters = parameters;
		this.options = options;
		if (tokens && tokens.length > 0) this.tokens = tokens.slice();
		else if (leafFn) this.tokens = [{
			t: "operand",
			v: this
		}];
		else this.tokens = [];
		this.summarizeFunction = (dc) => {
			return this.evaluateAsValue(dc);
		};
	}
	static leaf(leafFn, parameters, options) {
		return new SummarizeOperation(leafFn, parameters, options);
	}
	cloneWithTokens(newTokens) {
		return new SummarizeOperation(void 0, void 0, void 0, newTokens);
	}
	appendOp(op, operand) {
		const newTokens = this.tokens.slice();
		newTokens.push({
			t: "op",
			v: op
		});
		newTokens.push({
			t: "operand",
			v: operand
		});
		return this.cloneWithTokens(newTokens);
	}
	/**
	* Append addition to this expression.
	*
	* @param x - A numeric literal or another `SummarizeOperation` to add to this expression
	* @returns A new `SummarizeOperation` representing `(this + x)`
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     result: mean("a").add(10),
	*   }).observations
	* );
	* // [ { result: 13.33333 } ]
	* ```
	*/
	add(x) {
		return this.appendOp("+", x);
	}
	/**
	* Append subtraction to this expression.
	*
	* @param x - A numeric literal or another `SummarizeOperation` to subtract from this expression
	* @returns A new `SummarizeOperation` representing `(this - x)`
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     result: mean("a").sub(10),
	*   }).observations
	* );
	* // [ { result: -6.6667 } ]
	* ```
	*/
	sub(x) {
		return this.appendOp("-", x);
	}
	/**
	* Append multiplication to this expression. Multiplication has higher
	* precedence than addition/subtraction.
	*
	* @param x - A numeric literal or another `SummarizeOperation` to multiply with this expression
	* @returns A new `SummarizeOperation` representing `(this * x)`
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     result: mean("a").mul(10),
	*   }).observations
	* );
	* // [ { result: 33.3333 } ]
	* ```
	*/
	mul(x) {
		return this.appendOp("*", x);
	}
	/**
	* Append division to this expression.
	*
	* @param x - A numeric literal or another `SummarizeOperation` to divide this expression by
	* @returns A new `SummarizeOperation` representing `(this / x)`
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     result: mean("a").div(10),
	*   }).observations
	* );
	* // [ { result: .3333 } ]
	* ```
	*/
	div(x) {
		return this.appendOp("/", x);
	}
	/**
	* Append exponentiation (power) to this expression.
	*
	* Note: exponentiation uses right-associative semantics (a ^ b ^ c -> a ^ (b ^ c)).
	*
	* @param x - A numeric literal or another `SummarizeOperation` used as the exponent
	* @returns A new `SummarizeOperation` representing `(this ^ x)`
	*
	* @example
	* ```js
	* const d = [
	*   { a: 1, b: 2, c: 3 },
	*   { a: 0, b: 8, c: 3 },
	*   { a: 9, b: 4, c: 7 },
	* ];
	* const dc = new DataCalc(d);
	* console.log(
	*   dc.summarize({
	*     result: mean("a").pow(2),
	*   }).observations
	* );
	* // [ { result: 11.1111 } ]
	* ```
	*/
	pow(x) {
		return this.appendOp("^", x);
	}
	evaluateOperandToNumber(opd, dc) {
		if (typeof opd === "number") return opd;
		if (typeof opd === "string") {
			const m = /^__CHAIN_EXPR__\[(.*?)\]$/.exec(opd);
			if (m) {
				const payload = m[1];
				let ops = getChainOps(payload);
				if (!ops) try {
					ops = JSON.parse(decodeURIComponent(payload));
				} catch (_unused) {
					ops = void 0;
				}
				if (!ops) return NaN;
				let current = dc;
				let evaluated = void 0;
				for (const op of ops) {
					const method = current[op.name];
					if (typeof method !== "function") return NaN;
					const res = method.apply(current, op.args);
					if (res instanceof DataCalc) {
						current = res;
						continue;
					}
					if (Array.isArray(res)) {
						evaluated = res.length;
						break;
					}
					if (typeof res === "boolean") if (this.options && typeof this.options.coerceBooleans === "boolean" ? this.options.coerceBooleans : true) {
						evaluated = res ? 1 : 0;
						break;
					} else return NaN;
					evaluated = typeof res === "number" ? res : Number(res);
					break;
				}
				if (evaluated === void 0) evaluated = current instanceof DataCalc ? current.length : NaN;
				return typeof evaluated === "number" && !isNaN(evaluated) ? evaluated : NaN;
			}
		}
		const params = Array.isArray(opd.parameters) ? opd.parameters : opd.parameters === void 0 ? void 0 : [opd.parameters];
		const raw = opd.leafFn ? opd.leafFn(dc, params, opd.options) : opd.evaluateAsValue(dc);
		if (raw === null || raw === void 0) return NaN;
		const num = Number(raw);
		return typeof num === "number" && !isNaN(num) ? num : NaN;
	}
	evaluateFlatTokens(tokens, dc) {
		const operands = [];
		const ops = [];
		for (const tk of tokens) if (tk.t === "operand") operands.push(tk.v);
		else ops.push(tk.v);
		if (operands.length === 0) return NaN;
		while (ops.length > 0) {
			var _PRECEDENCE$ops$;
			let bestIdx = 0;
			let bestPrec = (_PRECEDENCE$ops$ = PRECEDENCE[ops[0]]) !== null && _PRECEDENCE$ops$ !== void 0 ? _PRECEDENCE$ops$ : 0;
			for (let i = 1; i < ops.length; i++) {
				var _PRECEDENCE$ops$i;
				const p = (_PRECEDENCE$ops$i = PRECEDENCE[ops[i]]) !== null && _PRECEDENCE$ops$i !== void 0 ? _PRECEDENCE$ops$i : 0;
				if (p > bestPrec || p === bestPrec && ops[i] === "^") {
					bestPrec = p;
					bestIdx = i;
				}
			}
			const op = ops.splice(bestIdx, 1)[0];
			const left = operands.splice(bestIdx, 1)[0];
			const right = operands.splice(bestIdx, 1)[0];
			const a = this.evaluateOperandToNumber(left, dc);
			const b = this.evaluateOperandToNumber(right, dc);
			let res = NaN;
			if (op === "+") res = a + b;
			else if (op === "-") res = a - b;
			else if (op === "*") res = a * b;
			else if (op === "/") res = b === 0 ? NaN : a / b;
			else if (op === "^") res = Math.pow(a, b);
			operands.splice(bestIdx, 0, res);
		}
		const final = operands[0];
		if (typeof final === "number") return final;
		return this.evaluateOperandToNumber(final, dc);
	}
	/**
	* Instance helper: return a grouped version of this operation so it becomes
	* a single operand in outer expressions. Equivalent to parens(this).
	*/
	parens() {
		return parens(this);
	}
	evaluateAsValue(dc) {
		if (this.leafFn) {
			const params = Array.isArray(this.parameters) ? this.parameters : this.parameters === void 0 ? void 0 : [this.parameters];
			const res = this.leafFn(dc, params, this.options);
			if (typeof res === "number" && Number.isNaN(res)) return null;
			return res;
		}
		if (!this.tokens || this.tokens.length === 0) return NaN;
		const val = this.evaluateFlatTokens(this.tokens, dc);
		if (typeof val === "number" && Number.isNaN(val)) return null;
		return val;
	}
};
/**
* Wraps an existing `SummarizeOperation` as a single grouped operand.
*
* @remarks Permits explicit parentheses in complex expressions.
*
* @param op - The `SummarizeOperation` to wrap
* @returns A new `SummarizeOperation` with the wrapped operand
*
* @example
* ```js
* const d = [
*   { a: 1 },
*   { a: 0 },
*   { a: 9 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
* // (mean a + min a) * 10
*     val: parens(mean("a").add(min("a"))).mul(10),
*   }).observations
* );
* // [ { val: 33.33333333333333 } ]
* ```
*/
function parens(op) {
	return new SummarizeOperation(void 0, void 0, void 0, [{
		t: "operand",
		v: op
	}]);
}
//#endregion
//#region src/SummarizeOperations.ts
/**
* Default options for summarize operations
*/
const DEFAULT_SUMMARIZE_OPTIONS = {
	coerceBooleans: true,
	skipMissing: false
};
/**
* Applies default options to user-provided options
*
* @param options - User provided options (may be undefined)
* @returns Options with defaults applied
*/
function applyDefaultOptions(options) {
	return _objectSpread2(_objectSpread2({}, DEFAULT_SUMMARIZE_OPTIONS), options);
}
function resolveLazy(param, dataCalc, opName) {
	if (typeof param !== "function") return param;
	try {
		const res = param(dataCalc);
		if (typeof res === "function") throw new M2Error(`${opName || "summarize()"}: lazy callback returned a function; expected a value or array of values.`);
		if (res instanceof DataCalc) throw new M2Error(`${opName || "summarize()"}: lazy callback returned a DataCalc instance; expected a value or array of values.`);
		return res;
	} catch (err) {
		throw new M2Error(`${opName || "summarize()"}: lazy callback threw an error: ${err && err.message ? err.message : String(err)}`);
	}
}
/**
* Helper function to process numeric values in a variable across observations
* Handles error checking, type coercion and missing values in a consistent way
*
* @param dataCalc - The DataCalc instance to process
* @param variable - The variable name to process
* @param options - Summarize options for the operation
* @param collector - Function that collects values (e.g., sum, max comparison)
* @param errorPrefix - Prefix for error messages
* @param initialState - Initial state for the collector function
* @returns An object containing the processed numeric values and count
*/
function processNumericValues(dataCalc, variable, options, collector, errorPrefix, initialState) {
	const mergedOptions = applyDefaultOptions(options);
	dataCalc.verifyObservationsContainVariable(variable);
	let count = 0;
	let state = initialState;
	let containsMissing = false;
	dataCalc.observations.forEach((o) => {
		if (dataCalc.isNonMissingNumeric(o[variable])) {
			state = collector(o[variable], state);
			count++;
			return;
		}
		if (typeof o[variable] === "boolean" && mergedOptions.coerceBooleans) {
			state = collector(o[variable] ? 1 : 0, state);
			count++;
			return;
		}
		if (dataCalc.isMissingNumeric(o[variable])) {
			containsMissing = true;
			return;
		}
		throw new M2Error(`${errorPrefix}: variable ${variable} has non-numeric value ${o[variable]} in this observation: ${JSON.stringify(o)}`);
	});
	return {
		state,
		count,
		containsMissing
	};
}
/**
* Processes an array of numeric values directly rather than through a
* variable name.
*/
function processDirectValues(values, options, collector, errorPrefix, initialState) {
	const mergedOptions = applyDefaultOptions(options);
	let state = initialState;
	let count = 0;
	let containsMissing = false;
	for (const value of values) if (typeof value === "number" && !isNaN(value) && isFinite(value)) {
		state = collector(value, state);
		count++;
	} else if (typeof value === "boolean" && mergedOptions.coerceBooleans) {
		state = collector(value ? 1 : 0, state);
		count++;
	} else if (value === null || value === void 0 || typeof value === "number" && (isNaN(value) || !isFinite(value))) containsMissing = true;
	else throw new M2Error(`${errorPrefix}: has non-numeric value ${value}`);
	return {
		state,
		count,
		containsMissing
	};
}
/**
* Processes a single value for summarize operations rather than through a
* variable name.
*/
function processSingleValue(value, options, errorPrefix) {
	const mergedOptions = applyDefaultOptions(options);
	if (typeof value === "number" && !isNaN(value) && isFinite(value)) return {
		value,
		isMissing: false
	};
	else if (typeof value === "boolean" && mergedOptions.coerceBooleans) return {
		value: value ? 1 : 0,
		isMissing: false
	};
	else if (value === null || value === void 0 || typeof value === "number" && (isNaN(value) || !isFinite(value))) return {
		value: 0,
		isMissing: true
	};
	else throw new M2Error(`${errorPrefix}: has non-numeric value ${value}`);
}
const nInternal = (dataCalc, params) => {
	const rawParam = params ? params[0] : void 0;
	if (typeof rawParam === "function") {
		const firstObs = dataCalc.observations[0];
		if (firstObs !== void 0) try {
			if (typeof rawParam(firstObs) === "boolean") {
				let count = 0;
				dataCalc.observations.forEach((o) => {
					if (rawParam(o)) count++;
				});
				return count;
			}
		} catch (_unused) {}
	}
	const variableOrValues = resolveLazy(rawParam, dataCalc);
	if (variableOrValues === void 0) return dataCalc.length;
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return 0;
		let count = 0;
		dataCalc.observations.forEach((o) => {
			if (o[variableOrValues] !== null && o[variableOrValues] !== void 0) count++;
		});
		return count;
	}
	if (Array.isArray(variableOrValues)) return variableOrValues.length;
	if (variableOrValues === null) return 0;
	return 1;
};
/**
* Calculates the number of observations.
*
* @returns summarize operation calculating the number of observations
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     count: n()
*   }).observations
* );
* // [ { count: 3 } ]
* ```
*/
function n(variableOrValues) {
	return SummarizeOperation.leaf(nInternal, [variableOrValues], void 0);
}
const sumInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const result = processNumericValues(dataCalc, variableOrValues, options, (value, sum) => sum + value, "sum()", 0);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else if (Array.isArray(variableOrValues)) {
		const result = processDirectValues(variableOrValues, options, (value, sum) => sum + value, "sum()", 0);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else {
		const result = processSingleValue(variableOrValues, options, "sum()");
		if (result.isMissing && !mergedOptions.skipMissing) return null;
		return result.isMissing ? null : result.value;
	}
};
/**
* Calculates the sum of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the sum
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     totalB: sum("b")
*   }).observations
* );
* // [ { totalB: 14 } ]
* ```
*/
function sum(variableOrValues, options) {
	return SummarizeOperation.leaf(sumInternal, [variableOrValues], options);
}
const meanInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const result = processNumericValues(dataCalc, variableOrValues, options, (value, sum) => sum + value, "mean()", 0);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state / result.count;
	} else if (Array.isArray(variableOrValues)) {
		const result = processDirectValues(variableOrValues, options, (value, sum) => sum + value, "mean()", 0);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state / result.count;
	} else {
		const result = processSingleValue(variableOrValues, options, "mean()");
		return result.isMissing && !mergedOptions.skipMissing ? null : result.value;
	}
};
/**
* Calculates the mean of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the mean
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     meanA: mean("a")
*   }).observations
* );
* // [ { meanA: 3.3333333333333335 } ]
* ```
*/
function mean(variableOrValues, options) {
	return SummarizeOperation.leaf(meanInternal, [variableOrValues], options);
}
const varianceInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const variable = variableOrValues;
		const meanResult = processNumericValues(dataCalc, variable, options, (value, sum) => sum + value, "variance()", 0);
		if (meanResult.containsMissing && !mergedOptions.skipMissing) return null;
		if (meanResult.count <= 1) return null;
		const meanValue = meanResult.state / meanResult.count;
		return processNumericValues(dataCalc, variable, options, (value, sum) => {
			const actualValue = typeof value === "boolean" && mergedOptions.coerceBooleans ? value ? 1 : 0 : value;
			return sum + Math.pow(actualValue - meanValue, 2);
		}, "variance()", 0).state / (meanResult.count - 1);
	} else if (Array.isArray(variableOrValues)) {
		const validValues = [];
		let containsMissing = false;
		for (const value of variableOrValues) if (typeof value === "number" && !isNaN(value) && isFinite(value)) validValues.push(value);
		else if (typeof value === "boolean" && mergedOptions.coerceBooleans) validValues.push(value ? 1 : 0);
		else if (value === null || value === void 0 || typeof value === "number" && (isNaN(value) || !isFinite(value))) containsMissing = true;
		else throw new M2Error(`variance(): has non-numeric value ${value}`);
		if (containsMissing && !mergedOptions.skipMissing) return null;
		if (validValues.length <= 1) return null;
		const mean = validValues.reduce((acc, val) => acc + val, 0) / validValues.length;
		return validValues.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (validValues.length - 1);
	} else return null;
};
/**
* Calculates the variance of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the variance
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
*   { a: 5, b: 0, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     varA: variance("a")
*   }).observations
* );
* // [ { varA: 16.916666666666668 } ]
* ```
*/
function variance(variableOrValues, options) {
	return SummarizeOperation.leaf(varianceInternal, [variableOrValues], options);
}
const minInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const result = processNumericValues(dataCalc, variableOrValues, options, (value, min) => min === Number.POSITIVE_INFINITY || value < min ? value : min, "min()", Number.POSITIVE_INFINITY);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else if (Array.isArray(variableOrValues)) {
		const result = processDirectValues(variableOrValues, options, (value, min) => min === Number.POSITIVE_INFINITY || value < min ? value : min, "min()", Number.POSITIVE_INFINITY);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else {
		const result = processSingleValue(variableOrValues, options, "min()");
		if (result.isMissing && !mergedOptions.skipMissing) return null;
		return result.isMissing ? null : result.value;
	}
};
/**
* Calculates the minimum value of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the minimum
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
*   { a: 5, b: 0, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     minA: min("a")
*   }).observations
* );
* // [ { minA: 0 } ]
* ```
*/
function min(variableOrValues, options) {
	return SummarizeOperation.leaf(minInternal, [variableOrValues], options);
}
const maxInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const result = processNumericValues(dataCalc, variableOrValues, options, (value, max) => max === Number.NEGATIVE_INFINITY || value > max ? value : max, "max()", Number.NEGATIVE_INFINITY);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else if (Array.isArray(variableOrValues)) {
		const result = processDirectValues(variableOrValues, options, (value, max) => max === Number.NEGATIVE_INFINITY || value > max ? value : max, "max()", Number.NEGATIVE_INFINITY);
		if (result.containsMissing && !mergedOptions.skipMissing) return null;
		if (result.count === 0) return null;
		return result.state;
	} else {
		const result = processSingleValue(variableOrValues, options, "max()");
		if (result.isMissing && !mergedOptions.skipMissing) return null;
		return result.isMissing ? null : result.value;
	}
};
/**
* Calculates the maximum value of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the maximum
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
*   { a: 5, b: 0, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     maxA: max("a")
*   }).observations
* );
* // [ { maxA: 9 } ]
* ```
*/
function max(variableOrValues, options) {
	return SummarizeOperation.leaf(maxInternal, [variableOrValues], options);
}
const medianInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	const mergedOptions = applyDefaultOptions(options);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const variable = variableOrValues;
		dataCalc.verifyObservationsContainVariable(variable);
		const values = [];
		let containsMissing = false;
		dataCalc.observations.forEach((o) => {
			if (dataCalc.isNonMissingNumeric(o[variable])) values.push(o[variable]);
			else if (typeof o[variable] === "boolean" && mergedOptions.coerceBooleans) values.push(o[variable] ? 1 : 0);
			else if (dataCalc.isMissingNumeric(o[variable])) containsMissing = true;
			else throw new M2Error(`median(): variable ${variable} has non-numeric value ${o[variable]} in this observation: ${JSON.stringify(o)}`);
		});
		if (containsMissing && !mergedOptions.skipMissing) return null;
		if (values.length === 0) return null;
		values.sort((a, b) => a - b);
		const mid = Math.floor(values.length / 2);
		if (values.length % 2 === 0) return (values[mid - 1] + values[mid]) / 2;
		else return values[mid];
	} else if (Array.isArray(variableOrValues)) {
		const values = [];
		let containsMissing = false;
		for (const value of variableOrValues) if (typeof value === "number" && !isNaN(value) && isFinite(value)) values.push(value);
		else if (typeof value === "boolean" && mergedOptions.coerceBooleans) values.push(value ? 1 : 0);
		else if (value === null || value === void 0 || typeof value === "number" && (isNaN(value) || !isFinite(value))) containsMissing = true;
		else throw new M2Error(`median(): has non-numeric value ${value}`);
		if (containsMissing && !mergedOptions.skipMissing) return null;
		if (values.length === 0) return null;
		values.sort((a, b) => a - b);
		const mid = Math.floor(values.length / 2);
		if (values.length % 2 === 0) return (values[mid - 1] + values[mid]) / 2;
		else return values[mid];
	} else {
		const result = processSingleValue(variableOrValues, options, "median()");
		if (result.isMissing && !mergedOptions.skipMissing) return null;
		return result.isMissing ? null : result.value;
	}
};
/**
* Calculates the median value of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the median
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
*   { a: 5, b: 0, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     medA: median("a")
*   }).observations
* );
* // [ { medA: 3 } ]
* ```
*/
function median(variableOrValues, options) {
	return SummarizeOperation.leaf(medianInternal, [variableOrValues], options);
}
const sdInternal = (dataCalc, params, options) => {
	let variableOrValues = params ? params[0] : void 0;
	variableOrValues = resolveLazy(variableOrValues, dataCalc);
	if (typeof variableOrValues === "string") {
		if (!dataCalc.variableExists(variableOrValues)) return null;
		const varianceValue = varianceInternal(dataCalc, params, options);
		if (varianceValue === null) return null;
		return Math.sqrt(varianceValue);
	} else if (Array.isArray(variableOrValues)) {
		const varianceValue = varianceInternal(dataCalc, params ? [...params] : [variableOrValues], options);
		if (varianceValue === null) return null;
		return Math.sqrt(varianceValue);
	} else return null;
};
/**
* Calculates the standard deviation of a variable, value, or array of values
*
* @param variableOrValues - name of variable, or alternatively, a value or
* array of values
* @param options - options for handling missing values and boolean coercion
* @returns summarize operation calculating the standard deviation
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
*   { a: 5, b: 0, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     sdA: sd("a")
*   }).observations
* );
* // [ { sdA: 4.112987559751022 } ]
* ```
*/
function sd(variableOrValues, options) {
	return SummarizeOperation.leaf(sdInternal, [variableOrValues], options);
}
/**
* Scalar / identity helper: wrap a numeric (or boolean) literal as a SummarizeOperation
* so it can participate in expression chaining (e.g. s(10).mul(mean('a')) ).
*/
const scalarInternal = (_dataCalc, params, options) => {
	let v = params ? params[0] : void 0;
	if (typeof v === "function") v = v(_dataCalc);
	if (typeof v === "string") {
		const m = /^__CHAIN_EXPR__\[(.*?)\]$/.exec(v);
		if (m) {
			const payload = m[1];
			let ops = getChainOps(payload);
			if (!ops) try {
				ops = JSON.parse(decodeURIComponent(payload));
			} catch (_unused2) {
				ops = void 0;
			}
			if (ops) {
				let current = _dataCalc;
				let evaluated = void 0;
				for (const op of ops) {
					const method = current[op.name];
					if (typeof method !== "function") {
						evaluated = NaN;
						break;
					}
					const res = method.apply(current, op.args);
					if (res instanceof DataCalc) {
						current = res;
						continue;
					}
					if (Array.isArray(res)) {
						evaluated = res.length;
						break;
					}
					if (typeof res === "boolean") {
						evaluated = res;
						break;
					}
					evaluated = typeof res === "number" ? res : Number(res);
					break;
				}
				if (evaluated === void 0) evaluated = current instanceof DataCalc ? current.length : NaN;
				if (!Number.isNaN(evaluated)) v = evaluated;
				else v = null;
			}
		}
	}
	if (typeof v === "object" && v !== null && "summarizeFunction" in v) try {
		const op = v;
		const paramsForOp = Array.isArray(op.parameters) ? op.parameters : op.parameters === void 0 ? void 0 : [op.parameters];
		v = op.summarizeFunction(_dataCalc, paramsForOp, op.options);
	} catch (_unused3) {
		v = null;
	}
	const result = processSingleValue(v, options, "scalar()");
	return result.isMissing ? null : result.value;
};
/**
* Wraps a scalar literal as a `SummarizeOperation` so it can participate in
* expression chaining with other summarize operations.
*
* @remarks Accepted inputs:
* - number: returned as-is
* - boolean: coerced to 1 (true) or 0 (false)
* - null/undefined: treated as a missing value and yields `null` when
*   evaluated in a summarize context
*
* `s` is provided as a shorthand alias for `scalar`.
*
* @example
* ```js
* const d = [
*   { a: 1, b: 2, c: 3 },
*   { a: 0, b: 8, c: 3 },
*   { a: 9, b: 4, c: 7 },
* ];
* const dc = new DataCalc(d);
* console.log(
*   dc.summarize({
*     meanA: scalar(10).mul(mean("a")),
*     meanA2: s(10).mul(mean("a")),
*   }).observations
* );
* // [ { meanA: 33.3333, meanA2: 33.3333 } ]
* ```
*/
function scalar(value) {
	return SummarizeOperation.leaf(scalarInternal, [value], void 0);
}
const s = scalar;
//#endregion
//#region src/index.ts
console.log("⚪ @m2c2kit/data-calc version 0.8.8 (35887002)");
//#endregion
export { DataCalc, SummarizeOperation, arrange, clearChainOps, distinct, filter, getChainOps, groupBy, max, mean, median, min, mutate, n, parens, pull, rename, s, scalar, sd, select, slice, sum, ungroup, variance };

//# sourceMappingURL=index.js.map